import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatErr } from "../lib/api";
import { Drawer } from "../components/UI";
import { Page } from "../components/AppShell";
import {
  Upload, Envelope, Warning, ShieldCheck, ClockClockwise, Stack, CheckCircle, XCircle,
  Robot, FileText, CircleNotch, ArrowsClockwise, DownloadSimple,
} from "@phosphor-icons/react";

const TABS = [
  { key: "processing", label: "PROCESSING", icon: CircleNotch, statuses: ["received", "extracting", "error"] },
  { key: "review_queue", label: "REVIEW QUEUE", icon: Stack, statuses: ["review_queue"] },
  { key: "rush_review", label: "RUSH", icon: ClockClockwise, statuses: ["rush_review"] },
  { key: "missing_info", label: "MISSING INFO", icon: Warning, statuses: ["missing_info"] },
  { key: "duplicate_review", label: "DUPLICATES", icon: ShieldCheck, statuses: ["duplicate_review"] },
  { key: "on_hold", label: "ON HOLD", icon: ClockClockwise, statuses: ["on_hold"] },
  { key: "approved", label: "APPROVED", icon: CheckCircle, statuses: ["approved"] },
  { key: "rejected", label: "REJECTED", icon: XCircle, statuses: ["rejected"] },
];

const PROCESSING_STATUSES = new Set(["received", "extracting"]);
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 120000; // 2 minutes hard cap

export default function Intake() {
  const [tab, setTab] = useState("review_queue");
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [pollAttempt, setPollAttempt] = useState(0);
  const [pollTimedOut, setPollTimedOut] = useState(false);
  const [oauthPolling, setOauthPolling] = useState(false);
  const [oauthMsg, setOauthMsg] = useState("");
  const [lastUploadedId, setLastUploadedId] = useState(null);
  const inputRef = useRef(null);
  const pollStartRef = useRef(null);

  const fetchAll = async () => {
    try {
      const r = await api.get("/intake");
      setAllItems(r.data);
      return r.data;
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchAll();
  }, []);

  // Determine whether polling should be active
  const hasProcessing = useMemo(
    () => allItems.some((it) => PROCESSING_STATUSES.has(it.status)),
    [allItems]
  );
  const pollingActive = hasProcessing && !pollTimedOut;

  // The polling loop: runs while we have items in flight, auto-stops on timeout.
  useEffect(() => {
    if (!pollingActive) {
      pollStartRef.current = null;
      return undefined;
    }
    if (!pollStartRef.current) pollStartRef.current = Date.now();
    const t = setInterval(async () => {
      const elapsed = Date.now() - (pollStartRef.current || Date.now());
      if (elapsed > POLL_MAX_MS) {
        setPollTimedOut(true);
        return;
      }
      setPollAttempt((n) => n + 1);
      await fetchAll();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [pollingActive]);

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadErr("");
    setPollTimedOut(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/intake/eml", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLastUploadedId(r.data?.id || null);
      setTab("processing"); // jump to processing tab so user sees the in-flight row
      await fetchAll();
    } catch (e) {
      setUploadErr(formatErr(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const onPollGmail = async () => {
    setOauthPolling(true); setOauthMsg("");
    try {
      const r = await api.post("/intake/poll-gmail", {});
      const { created = 0, merged = 0, skipped = 0, errors = [], message } = r.data || {};
      if (message && created === 0 && merged === 0 && skipped === 0 && errors.length === 0) {
        setOauthMsg(message);
      } else {
        const parts = [];
        parts.push(`${created} new`);
        if (merged) parts.push(`${merged} merged`);
        if (skipped) parts.push(`${skipped} skipped`);
        if (errors.length) parts.push(`${errors.length} errors`);
        setOauthMsg(parts.join(" · "));
      }
      if (created > 0 || merged > 0) setTab("processing");
      await fetchAll();
    } catch (e) { setOauthMsg(formatErr(e)); }
    finally { setOauthPolling(false); }
  };

  // Bucket items per tab for accurate counts
  const buckets = useMemo(() => {
    const acc = Object.fromEntries(TABS.map((t) => [t.key, []]));
    for (const it of allItems) {
      for (const t of TABS) {
        if (t.statuses.includes(it.status)) acc[t.key].push(it);
      }
    }
    return acc;
  }, [allItems]);

  const items = buckets[tab] || [];

  return (
    <Page
      title="AI Intake & Gatekeeper Review"
      subtitle="EMAIL → DRAFT JOB → QUALITY-CONTROL REVIEW"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Intake"]}
      testid="intake-page"
      actions={
        <>
          <button
            className="btn btn-ghost"
            disabled={oauthPolling}
            onClick={onPollGmail}
            data-testid="poll-gmail-btn"
            title="Pull new emails from connected Gmail accounts"
          >
            <DownloadSimple size={14} /> {oauthPolling ? "POLLING…" : "POLL GMAIL"}
          </button>
          <label className="btn btn-primary cursor-pointer" data-testid="upload-eml-btn">
            <Upload size={14} /> {uploading ? "UPLOADING…" : "UPLOAD .EML"}
            <input
              ref={inputRef}
              type="file"
              accept=".eml,message/rfc822"
              hidden
              disabled={uploading}
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid="eml-file-input"
            />
          </label>
        </>
      }
    >
      {uploadErr && <div className="text-xs text-destructive mb-3" data-testid="upload-error">{uploadErr}</div>}
      {oauthMsg && <div className="text-xs text-muted-foreground mb-3 mono" data-testid="oauth-poll-status">GMAIL: {oauthMsg}</div>}

      {/* Polling banner */}
      {pollingActive && (
        <div
          className="flex items-center gap-2 px-3 py-2 mb-3 border border-border bg-muted/30 text-xs"
          data-testid="polling-banner"
        >
          <CircleNotch size={14} className="animate-spin" />
          <span className="mono">
            AI EXTRACTING · {buckets.processing.length} JOB{buckets.processing.length === 1 ? "" : "S"} IN FLIGHT · POLL #{pollAttempt}
          </span>
          <button
            className="ml-auto btn btn-ghost btn-sm"
            onClick={() => { setPollAttempt((n) => n + 1); fetchAll(); }}
            data-testid="refresh-now-btn"
          >
            <ArrowsClockwise size={12} /> REFRESH
          </button>
        </div>
      )}
      {pollTimedOut && (
        <div className="flex items-center gap-2 px-3 py-2 mb-3 border border-amber-500 bg-amber-50 text-xs text-amber-800" data-testid="poll-timeout-banner">
          <Warning size={14} />
          <span>Extraction is taking longer than expected. Refresh manually or try again later.</span>
          <button className="ml-auto btn btn-ghost btn-sm" onClick={() => { setPollTimedOut(false); fetchAll(); }} data-testid="poll-timeout-refresh">
            <ArrowsClockwise size={12} /> REFRESH
          </button>
        </div>
      )}

      <div className="flex gap-1 border-b border-border mb-4 overflow-x-auto" data-testid="intake-tabs">
        {TABS.map((t) => {
          const count = buckets[t.key]?.length || 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-xs mono uppercase flex items-center gap-2 border-b-2 transition-colors ${
                tab === t.key ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`tab-${t.key}`}
            >
              <t.icon size={12} className={t.key === "processing" && pollingActive ? "animate-spin" : ""} />
              {t.label}
              <span className="mono ml-1" data-testid={`tab-${t.key}-count`}>({count})</span>
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="p-8 text-center text-muted-foreground" data-testid="intake-loading">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-12 text-center text-muted-foreground surface" data-testid="intake-empty">
          <Envelope size={28} weight="thin" className="mx-auto mb-2" />
          {tab === "processing" ? (
            <>No intakes are currently being processed. Upload an .eml or poll Gmail to start.</>
          ) : (
            <>No intakes in <span className="mono">{tab}</span>.</>
          )}
        </div>
      ) : (
        <div className="surface">
          <table className="dense">
            <thead><tr><th>SENDER</th><th>SUBJECT</th><th>SUMMARY</th><th>STATUS</th><th>CONF</th><th>FLAGS</th><th>RECEIVED</th></tr></thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  data-testid={`intake-row-${it.id}`}
                  className={`cursor-pointer hover:bg-muted/30 ${lastUploadedId === it.id ? "bg-blue-50" : ""}`}
                  onClick={() => setDetailId(it.id)}
                >
                  <td className="text-xs">{(it.sender || "").slice(0, 40)}</td>
                  <td className="text-xs truncate" style={{ maxWidth: 220 }}>{it.subject || "—"}</td>
                  <td className="text-xs text-muted-foreground truncate" style={{ maxWidth: 360 }}>
                    {it.ai_summary || (PROCESSING_STATUSES.has(it.status) ? "extracting…" : (it.status === "error" ? "extraction failed" : "—"))}
                  </td>
                  <td className="text-xs mono uppercase" data-testid={`intake-status-${it.id}`}>
                    {PROCESSING_STATUSES.has(it.status) ? (
                      <span className="flex items-center gap-1">
                        <CircleNotch size={10} className="animate-spin" /> {it.status}
                      </span>
                    ) : it.status}
                  </td>
                  <td className="mono text-xs">{it.confidence_overall ?? "—"}</td>
                  <td className="text-xs">
                    {it.is_rush && <span className="badge badge-default mr-1" style={{ background: "#FEE2E2", color: "#B91C1C" }}>RUSH</span>}
                    {it.duplicate_warning && <span className="badge badge-default" style={{ background: "#FEF3C7", color: "#92400E" }}>DUP</span>}
                  </td>
                  <td className="text-xs text-muted-foreground mono">{new Date(it.received_at).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <IntakeDetailDrawer key={detailId || "closed"} id={detailId} onClose={() => setDetailId(null)} onChange={fetchAll} />
    </Page>
  );
}

/* -------------------------------------------------------------------------- */
/* Detail drawer with its own polling loop while extraction is in progress.    */
/* -------------------------------------------------------------------------- */

function IntakeDetailDrawer({ id, onClose, onChange }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reason, setReason] = useState("");
  const [activeJobIdx, setActiveJobIdx] = useState(0);
  const [pollCount, setPollCount] = useState(0);
  const [refreshTick, setRefreshTick] = useState(0);

  // Initial fetch + refresh trigger. Parent owns whether the drawer is shown,
  // and we use a `key` on this component to remount when the id changes so we
  // never have to reset state inside this effect.
  useEffect(() => {
    if (!id) return undefined;
    let cancelled = false;
    api.get(`/intake/${id}`)
      .then((r) => { if (!cancelled) setD(r.data); })
      .catch((e) => { if (!cancelled) setErr(formatErr(e)); });
    return () => { cancelled = true; };
  }, [id, refreshTick]);

  // Auto-poll the detail when extraction is in flight
  const status = d?.intake?.status;
  const stillProcessing = status === "received" || status === "extracting";
  useEffect(() => {
    if (!stillProcessing || !id) return undefined;
    if (pollCount > 40) return undefined; // ~100s cap on detail polling
    const t = setTimeout(() => {
      setPollCount((n) => n + 1);
      setRefreshTick((n) => n + 1);
    }, 2500);
    return () => clearTimeout(t);
  }, [stillProcessing, id, pollCount, refreshTick]);

  const act = async (path, body = {}) => {
    setBusy(true); setErr("");
    try {
      await api.post(`/intake/${id}/${path}`, body);
      onChange && onChange();
      onClose();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  const e = d?.intake;
  const x = d?.extraction;
  const extracted = x?.extracted_json || {};
  const jobs = Array.isArray(extracted.jobs) ? extracted.jobs : [];
  const job = jobs[activeJobIdx] || {};

  return (
    <Drawer
      open={!!id}
      onClose={onClose}
      testid="intake-drawer"
      title={e ? `Intake · ${(e.subject || "(no subject)").slice(0, 60)}` : "Loading…"}
      footer={e && e.status !== "approved" && e.status !== "rejected" && !stillProcessing ? (
        <>
          <input
            className="input"
            placeholder="reason / notes"
            value={reason}
            onChange={(ev) => setReason(ev.target.value)}
            style={{ flex: 1 }}
            data-testid="decision-reason"
          />
          <button className="btn btn-ghost" disabled={busy} onClick={() => act("hold", { reason })} data-testid="hold-btn">HOLD</button>
          <button className="btn btn-ghost" disabled={busy} onClick={() => act("reject", { reason })} data-testid="reject-btn">REJECT</button>
          <button className="btn btn-primary" disabled={busy} onClick={() => act("approve")} data-testid="approve-btn">
            <CheckCircle size={14} /> APPROVE & ACTIVATE {jobs.length > 1 ? `(${jobs.length} JOBS)` : ""}
          </button>
        </>
      ) : null}
    >
      {!d ? (
        <div className="py-10 text-muted-foreground text-center" data-testid="drawer-loading">Loading…</div>
      ) : (
        <div className="drawer-section-grid">
          {/* Multi-job switcher */}
          {jobs.length > 1 && (
            <div className="flex gap-1 mb-3" data-testid="job-switcher">
              {jobs.map((_, i) => (
                <button
                  key={i}
                  className={`px-2 py-1 text-xs mono border ${activeJobIdx === i ? "border-primary text-primary" : "border-border text-muted-foreground"}`}
                  onClick={() => setActiveJobIdx(i)}
                  data-testid={`job-tab-${i}`}
                >
                  JOB {i + 1} / {jobs.length}
                </button>
              ))}
            </div>
          )}

          {/* AI summary */}
          <Section title="AI SUMMARY" icon={Robot} wide testid="sec-summary">
            {stillProcessing ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="extracting-indicator">
                <CircleNotch size={14} className="animate-spin" />
                Extraction in progress (GPT-5.2)… poll #{pollCount}
              </div>
            ) : (
              <>
                <div className="text-sm whitespace-pre-wrap" data-testid="ai-summary-text">{x?.ai_summary || "No summary yet."}</div>
                {x && (
                  <div className="text-[10px] text-muted-foreground mt-2 mono" data-testid="ai-confidence">
                    CONFIDENCE: {x.confidence_overall} · STATUS: {e.status}
                    {e.thread_count && e.thread_count > 1 ? ` · THREAD MSGS: ${e.thread_count}` : ""}
                  </div>
                )}
                {x?.extraction_notes && <div className="text-xs text-muted-foreground italic mt-2">{x.extraction_notes}</div>}
              </>
            )}
          </Section>

          {/* Case */}
          <Section title="CASE" icon={FileText} testid="sec-case">
            <Field label="CASE NUMBER" v={job.case?.case_number} />
            <Field label="COURT" v={job.case?.court} />
            <Field label="PLAINTIFF" v={job.case?.plaintiff} />
            <Field label="DEFENDANT" v={job.case?.defendant} />
            <Field label="JUDGE" v={job.case?.judge} />
            <Field label="FILING DATE" v={job.case?.filing_date} />
          </Section>

          {/* Service */}
          <Section title="SERVICE" icon={ShieldCheck} testid="sec-service">
            <Field label="SERVICE TYPE" v={job.service?.service_type} />
            <Field label="ADDRESS" v={job.service?.service_address} />
            <Field label="DEADLINE" v={job.service?.deadline} />
            <Field label="APPEARANCE" v={job.service?.appearance_date} />
            <Field label="INSTRUCTIONS" v={job.service?.special_instructions} />
          </Section>

          {/* Customer */}
          <Section title="CUSTOMER" icon={Envelope} testid="sec-customer">
            <Field label="ORG" v={job.customer?.organization} />
            <Field label="ATTORNEY" v={job.customer?.attorney_name} />
            <Field label="ATTY EMAIL" v={job.customer?.attorney_email} />
            <Field label="PHONE" v={job.customer?.contact_phone} />
          </Section>

          {/* Flags & gaps */}
          <Section title="FLAGS & GAPS" icon={Warning} wide testid="sec-flags">
            {x?.duplicate_warning && (
              <div className="border border-amber-500 bg-amber-50 p-2 mb-2" data-testid="dup-warning">
                <div className="text-xs mono uppercase text-amber-800">DUPLICATE — {x.duplicate_warning.level}</div>
                <div className="text-xs">{x.duplicate_warning.reason}</div>
              </div>
            )}
            <div className="text-xs">
              <span className="label-xs">MISSING:</span>{" "}
              {(job.missing_fields || x?.missing_fields || []).length
                ? (job.missing_fields || x?.missing_fields).join(", ")
                : <span className="text-muted-foreground">none</span>}
            </div>
            <div className="text-xs mt-1">
              <span className="label-xs">RISKS:</span>{" "}
              {(job.risk_flags || x?.risk_flags || []).length
                ? (job.risk_flags || x?.risk_flags).map((r) => (
                  <span key={r} className="badge badge-default mr-1" style={{ background: "#FEE2E2", color: "#B91C1C" }}>{r}</span>
                ))
                : <span className="text-muted-foreground">none</span>}
            </div>
          </Section>

          {/* Activity log */}
          <Section title="ACTIVITY" icon={ClockClockwise} wide testid="sec-activity">
            {(d.events || []).length === 0
              ? <div className="text-xs text-muted-foreground">No events yet.</div>
              : d.events.map((ev, i) => (
                <div key={i} className="flex justify-between py-1 border-b border-border last:border-0">
                  <span className="text-xs mono">{ev.event_type}</span>
                  <span className="text-[10px] text-muted-foreground mono">{new Date(ev.created_at).toLocaleString()}</span>
                </div>
              ))}
            <button
              className="btn btn-ghost btn-sm mt-2"
              onClick={() => setRefreshTick((n) => n + 1)}
              data-testid="refresh-detail"
            >
              <ArrowsClockwise size={12} /> REFRESH
            </button>
          </Section>

          {err && <div className="text-xs text-destructive">{err}</div>}
        </div>
      )}
    </Drawer>
  );
}

const Section = ({ title, icon: I, children, testid, wide }) => (
  <div className={`surface p-4 ${wide ? "drawer-section--wide" : ""}`} data-testid={testid}>
    <div className="flex items-center gap-2 mb-2"><I size={14} /><span className="label-xs">{title}</span></div>
    {children}
  </div>
);

const Field = ({ label, v }) => (
  <div className="flex justify-between text-xs py-1 border-b border-border last:border-0">
    <span className="label-xs">{label}</span>
    <span className="text-right text-muted-foreground">{v || "—"}</span>
  </div>
);
